import React, { useState } from 'react';
import ControlTierSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/initialization/ControlTierSelection';
import InitializationFormReview from 'components/admin/deviceDashboard/deviceEditor/simulator/initialization/InitializationFormReview';
import MachineNameSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/initialization/MachineNameSelection';
import MachineStyleSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/initialization/MachineStyleSelection';
import CentsLoader from 'components/common/centsLoader/CentsLoader';

export const ElectroluxSerialDeviceInitializationSimulator = (props) => {
  const { device, manufacturer, onBack, onSubmit, machineStyles: fullMachineStyles, controlTiers } = props;
  const [loading, setLoading] = useState(false);
  const electroluxInitializationScreens = {
    MACHINE_STYLE: 'MACHINE_STYLE',
    CONTROL_TIER: 'CONTROL_TIER',
    MACHINE_NAME: 'MACHINE_NAME',
    FINAL_REVIEW: 'FINAL_REVIEW',
  };
  const [initializationScreen, setInitializationScreen] = useState(
    electroluxInitializationScreens.MACHINE_STYLE,
  );
  
  // electrolux dryers are dumb-dumb, so excluding here
  const machineStyles = fullMachineStyles.filter((style) => !style.displayValue.includes('Dryer'));

  // form data
  const [selectedMachineStyle, setSelectedMachineStyle] = useState(null);
  const [selectedMachineStyleDisplay, setSelectedMachineStyleDisplay] = useState(null);
  const [selectedMachineType, setSelectedMachineType] = useState('WASHER');
  const [selectedControlTier, setSelectedControlTier] = useState(null);
  const [selectedControlTierDisplay, setSelectedControlTierDisplay] = useState(null);
  const [selectedMachineName, setSelectedMachineName] = useState(null);

  /**
   * Depending on the current wizard screen, set the proper screen to show
   */
  const handleWizardGoBackFunctionality = () => {
    switch (initializationScreen) {
      case electroluxInitializationScreens.MACHINE_STYLE:
        return onBack();

      case electroluxInitializationScreens.CONTROL_TIER:
        return setInitializationScreen(electroluxInitializationScreens.MACHINE_STYLE);

      case electroluxInitializationScreens.MACHINE_NAME:
        return setInitializationScreen(electroluxInitializationScreens.CONTROL_TIER);

      case electroluxInitializationScreens.FINAL_REVIEW:
        return setInitializationScreen(electroluxInitializationScreens.MACHINE_NAME);

      default:
        return onBack();
    }
  };

  /**
   * disable the submit functionality if any of the following is missing or null:
   *
   * 1) manufacturer
   * 2) machine style
   * 3) machine type
   * 4) machine name
   * 5) control tier
   */
  const disableFinalSubmitButton = () => {
    return (
      !manufacturer ||
      !selectedMachineStyle ||
      !selectedMachineType ||
      !selectedMachineName ||
      !selectedControlTier
    );
  };

  /**
   * compile the form data and send back to DeviceSimulator
   */
  const submitDeviceInitializationFormData = () => {
    const finalData = {
      deviceId: device.id,
      manufacturer,
      machineStyle: selectedMachineStyle,
      machineType: selectedMachineType,
      controlTier: selectedControlTier,
      lowerMachineName: selectedMachineName,
    };

    return onSubmit(finalData);
  };

  /**
   * render a summary screen for the user to review before submitting
   */
  const renderFinalDataReview = () => {
    return (
      <InitializationFormReview
        manufacturer={manufacturer}
        selectedMachineStyleDisplay={selectedMachineStyleDisplay}
        selectedControlTierDisplay={selectedControlTierDisplay}
        selectedMachineName={selectedMachineName}
        selectedMachineType={selectedMachineType}
        onBack={handleWizardGoBackFunctionality}
        onNext={submitDeviceInitializationFormData}
        disableFinalSubmitButton={disableFinalSubmitButton}
      />
    );
  };

  /**
   * navigate to the final review screen when moving forward after machine name selection
   */
  const handleNextOnMachineNameSelection = () => {
    setInitializationScreen(electroluxInitializationScreens.FINAL_REVIEW);
  };

  /**
   * render the various control tiers that can be selected for a given machine manufacturer
   */
  const renderMachineNameSelection = () => {
    return (
      <MachineNameSelection
        onBack={handleWizardGoBackFunctionality}
        onNext={handleNextOnMachineNameSelection}
        selectedMachineName={selectedMachineName}
        setSelectedMachineName={setSelectedMachineName}
        title={"Choose your machine's name"}
      />
    );
  };

  /**
   * when navigating next on control tier selection, we render the machine size selection UI
   */
  const handleNextOnControlTierSelection = async () => {
    setInitializationScreen(electroluxInitializationScreens.MACHINE_NAME);
  };

  /**
   * save the selected control tier rawValue in state
   *
   * @param {object} controlTierData
   */
  const handleControlTierSelection = (controlTierData) => {
    setSelectedControlTier(controlTierData.rawValue);
    setSelectedControlTierDisplay(controlTierData.displayValue);
  };

  /**
   * render the various control tiers that can be selected for a given machine manufacturer
   */
  const renderControlTierSelection = () => {
    return (
      <ControlTierSelection
        controlTiers={controlTiers}
        selectedControlTierDisplay={selectedControlTierDisplay}
        onBack={handleWizardGoBackFunctionality}
        onNext={handleNextOnControlTierSelection}
        onValueChange={handleControlTierSelection}
        selectedControlTier={selectedControlTier}
      />
    );
  };

  /**
   * set the selected machine style and then based on the displayValue of machine style,
   * set the machine type
   *
   * @param {object} machineStyleData
   */
  const handleMachineStyleSelection = (machineStyleData) => {
    setSelectedMachineStyle(machineStyleData.rawValue);
    setSelectedMachineStyleDisplay(machineStyleData.displayValue);

    // setting these as null because machine style often determines the control tiers applicable
    setSelectedControlTier(null);
    setSelectedControlTierDisplay(null);
  };

  /**
   * when clicking next after selecting machine style, move forward to control tier selection
   */
  const handleNextOnMachineStyleSelection = () => {
    setInitializationScreen(electroluxInitializationScreens.CONTROL_TIER);
  };

  /**
   * render the dropdown and wizard view for machine style selection
   */
  const renderMachineStyleSelection = () => {
    return (
      <MachineStyleSelection
        machineStyles={machineStyles}
        selectedMachineStyleDisplay={selectedMachineStyleDisplay}
        onBack={handleWizardGoBackFunctionality}
        onNext={handleNextOnMachineStyleSelection}
        onValueChange={handleMachineStyleSelection}
        selectedMachineStyle={selectedMachineStyle}
      />
    );
  };

  /**
   * Determine the proper form/wizard step based on state of the component
   */
  const renderInitializationWizardOptions = () => {
    switch (initializationScreen) {
      case electroluxInitializationScreens.MACHINE_STYLE:
        return renderMachineStyleSelection();

      case electroluxInitializationScreens.CONTROL_TIER:
        return renderControlTierSelection();

      case electroluxInitializationScreens.MACHINE_NAME:
        return renderMachineNameSelection();

      case electroluxInitializationScreens.FINAL_REVIEW:
        return renderFinalDataReview();

      default:
        return renderMachineStyleSelection();
    }
  };

  return <>{loading ? <CentsLoader /> : renderInitializationWizardOptions()}</>;
};
